import { css } from '@emotion/react';
import { Flex, SEMANTIC_BREAKPOINTS, maxMediaQuery } from '@inflearn/ds-react-next';
import type { ParseKeys } from 'i18next';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import AppShellLayoutForCSR from '../AppShellLayoutForCSR';
import CustomHead, { type CustomHeadProps } from '../CustomHead';
import { DefaultGuard } from '../DefaultLayout';
import Header from '../Header';

interface LayoutProps {
  guard?: ({ children }: { children: ReactNode }) => JSX.Element;
  meta: CustomHeadProps & { title: ParseKeys; description: ParseKeys; siteTitle?: ParseKeys };
  /**
   * 이미 가입한 사용자가 머무는 화면에서 앱쉘 헤더를 노출한다. 헤더에 로고가 있어 자체 로고 줄은 숨긴다.
   * 로그인 여부 처리는 페이지가 맡는다.
   */
  withAppShellHeader?: boolean;
  children: ReactNode;
}

const Layout = ({
  guard: Guard = DefaultGuard,
  meta,
  withAppShellHeader = false,
  children,
}: LayoutProps) => {
  const { t } = useTranslation();

  const content = (
    <Guard>
      <Flex
        direction="column"
        align="center"
        px={16}
        py={72}
        mx="auto"
        w={360}
        styles={{
          root: styleLayout,
        }}
      >
        {!withAppShellHeader && <Header />}
        {children}
      </Flex>
    </Guard>
  );

  return (
    <>
      <CustomHead
        {...meta}
        title={t(meta.title)}
        description={t(meta.description)}
        siteTitle={t(meta.siteTitle || '인프런')}
        pageCategory={t('메타.계정.페이지카테고리')}
      />
      {withAppShellHeader ? (
        <AppShellLayoutForCSR
          requiredLogin={false}
          showChanneltalk={false}
          showFooter={false}
          disableBottomNavigation
        >
          {content}
        </AppShellLayoutForCSR>
      ) : (
        content
      )}
    </>
  );
};

const styleLayout = css({
  [maxMediaQuery(SEMANTIC_BREAKPOINTS.mobile)]: {
    padding: '32px 16px',
    width: '100%',
  },
});

export default Layout;
